import { FormControl, DefaultFormFields, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { duplicateProductTemplate } from 'api/operations/productServiceV2';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { getCurrentDateInMDTFormat } from 'global/utils/dateHelper';
import { ProductTemplate } from 'types/productServiceV2';
import { PushFn } from './browsingStore';

interface SaveProposalFields extends DefaultFormFields {
  internalName: FormControl<string>;
  externalName: FormControl<string>;
}

type ShortTemplateInfo = Pick<ProductTemplate, 'id' | 'name' | 'internalName'>;

export default class AddVariantModalStore {
  rootStore: RootStore;

  form: FormController<SaveProposalFields>;
  loading = false;
  showAddVariantModal = false;
  shortTemplateInfo: ShortTemplateInfo | null = null;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setShortTemplateInfo(templateInfo: ShortTemplateInfo | null): void {
    this.shortTemplateInfo = templateInfo;
  }

  private setShowAddVariantModal(status: boolean): void {
    this.showAddVariantModal = status;
  }

  public get duplicatedTemplateName(): string {
    return this.shortTemplateInfo?.name || '';
  }

  public openAddVariantModal({ id, name, internalName }: ShortTemplateInfo): void {
    this.setShowAddVariantModal(true);
    this.setShortTemplateInfo({ id, name, internalName });

    this.form.populateFields({
      externalName: `${name} ${getCurrentDateInMDTFormat()}`,
    });
  }

  public async confirmAddVariantModal(
    params: { periodId: string; cartId: string; groupId: string },
    pushFn: PushFn,
  ): Promise<void> {
    if (this.shortTemplateInfo?.id) {
      this.setLoading(true);
      const duplicatedTemplate = await duplicateProductTemplate({
        id: this.shortTemplateInfo.id,
        externalName: this.form.fields.externalName.value,
        internalName: this.form.fields.internalName.value,
        groupId: params.groupId || null,
      });

      runInAction(() => {
        this.setLoading(false);
        this.closeAddVariantModal();
        const newTemplateId = duplicatedTemplate?.data?.duplicateProductTemplate.id || '';
        pushFn(`/product-template-checklist/${newTemplateId}?type=browsing`);
        // Set necessary parameters in local storage to persist them across the product template pages
        localStorage.setItem('params', JSON.stringify({ ...params, type: 'browsing' }));
      });
    }
  }

  public closeAddVariantModal(): void {
    this.setShowAddVariantModal(false);
    this.form.resetForm();
    this.setShortTemplateInfo(null);
  }

  private isExtNameDifferentFromMainPlan(value: string): boolean {
    return value.trim() !== this.shortTemplateInfo?.name.trim();
  }

  public get isModalFormValid(): boolean {
    return !this.loading && this.form.basicFieldsValidationsPassed;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.form = new FormController({
      internalName: new FormControl<string>('', {
        optional: false,
        validations: ['printableCharacters'],
        customValidations: [
          {
            check: this.isExtNameDifferentFromMainPlan.bind(this),
            msg: 'New name must not match the original.',
          },
        ],
      }),
      externalName: new FormControl<string>('', {
        optional: false,
        validations: ['printableCharacters'],
        customValidations: [
          {
            check: this.isExtNameDifferentFromMainPlan.bind(this),
            msg: 'New name must not match the original.',
          },
        ],
      }),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
